import { useEffect, useRef, useState } from "react";

const degrees = [
  { institution: "University of South Carolina", degree: "Bachelor of Arts", field: "English and Anthropology", year: "2009" },
  { institution: "Liberty University", degree: "Master of Arts", field: "Ethnomusicology", year: "2014" },
  { institution: "Liberty University", degree: "Certificate of Graduate Studies", field: "Worship Studies", year: "2014" },
  { institution: "Wake Technical Community College", degree: "Associates of Applied Arts", field: "Cybersecurity", year: "May 2024" },
  { institution: "Wake Technical Community College", degree: "Associates of Applied Arts", field: "Network Management", year: "May 2024" },
  { institution: "Wake Technical Community College", degree: "Certificate", field: "Information Technology: Penetration Testing & Ethical Hacking", year: "May 2024" },
  { institution: "Wake Technical Community College", degree: "Certificate", field: "Information Technology: Cisco Network Associate", year: "May 2024" },
  { institution: "Wake Technical Community College", degree: "Certificate", field: "Information Technology: Wireless Network Certification", year: "May 2024" },
  { institution: "Wake Technical Community College", degree: "Certificate", field: "Information Technology: Cyber Operations & Digital Forensics", year: "May 2024" },
];

const certifications = [
  { issuer: "Coursera", name: "Google IT Support", year: "2018" },
  { issuer: "Coursera", name: "IBM Data Science", year: "2020" },
  { issuer: "Cisco", name: "CCNA: Introduction to Networks", year: "2022" },
  { issuer: "Cisco", name: "CCNA: Switching, Routing, and Wireless Essentials", year: "2022" },
  { issuer: "Cisco", name: "CyberOps Associate", year: "2023" },
  { issuer: "Cisco", name: "CCNA: Enterprise Networking, Security, and Automation", year: "2023" },
  { issuer: "Certiport", name: "IT Specialist – Python", year: "2023" },
  { issuer: "ISC2", name: "Certified in Cybersecurity", year: "2024" },
  { issuer: "NCBFAA", name: "Master Customs Specialist", year: "2024" },
  { issuer: "CompTIA", name: "Security+ (SY0-701)", year: "2025" },
  { issuer: "NCBFAA", name: "Certified Zone Specialist", year: "2026" },
];

export default function Education() {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const [activeTab, setActiveTab] = useState<"education" | "certifications">("education");

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.08 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="education" className="py-28 md:py-36 bg-white" ref={ref}>
      <div className="max-w-7xl mx-auto px-6 md:px-10">

        {/* Section label */}
        <div className={`flex items-center gap-4 mb-6 transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <span className="text-[11px] uppercase tracking-[0.3em] text-[#C9A961]" style={{ fontFamily: "'Lato', sans-serif" }}>
            03 / Education
          </span>
          <div className="flex-1 h-px bg-[#C9A961]/25" />
        </div>

        <div className={`mb-14 transition-all duration-700 delay-100 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <h2
            className="font-serif text-4xl md:text-5xl text-[#2B1810] leading-tight mb-4"
            style={{ fontFamily: "'Cormorant Garamond', serif", letterSpacing: "-0.01em" }}
          >
            Always Learning, Always Growing
          </h2>
          <p className="text-base text-[#B8A5A8] max-w-xl" style={{ fontFamily: "'Lato', sans-serif" }}>
            From English and Anthropology to Cybersecurity and Customs Law — a lifelong commitment to expanding what&apos;s possible.
          </p>
        </div>

        {/* Tab switcher */}
        <div className={`mb-12 transition-all duration-700 delay-200 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="inline-flex items-center p-1 py-1 px-1 bg-[#FAF7F2] rounded-full border border-[#C9A961]/20">
            <button
              onClick={() => setActiveTab("education")}
              className={`px-6 py-2 rounded-full text-[12px] uppercase tracking-widest transition-all duration-300 cursor-pointer whitespace-nowrap ${
                activeTab === "education"
                  ? "bg-[#2B1810] text-[#FAF7F2]"
                  : "text-[#2B1810]/50 hover:text-[#2B1810]"
              }`}
              style={{ fontFamily: "'Lato', sans-serif" }}
            >
              Degrees & Diplomas
            </button>
            <button
              onClick={() => setActiveTab("certifications")}
              className={`px-6 py-2 rounded-full text-[12px] uppercase tracking-widest transition-all duration-300 cursor-pointer whitespace-nowrap ${
                activeTab === "certifications"
                  ? "bg-[#2B1810] text-[#FAF7F2]"
                  : "text-[#2B1810]/50 hover:text-[#2B1810]"
              }`}
              style={{ fontFamily: "'Lato', sans-serif" }}
            >
              Certifications
            </button>
          </div>
        </div>

        {/* Education tab */}
        {activeTab === "education" && (
          <div className={`transition-all duration-500 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {degrees.map((d, i) => (
                <div
                  key={i}
                  className="flex items-start gap-4 p-5 rounded-xl bg-[#FAF7F2] border border-[#C9A961]/10 hover:border-[#C9A961]/35 transition-colors duration-300"
                >
                  <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-full bg-[#C9A961]/12">
                    <i className="ri-graduation-cap-line text-[#C9A961] text-base" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-sm font-medium text-[#2B1810] leading-snug" style={{ fontFamily: "'Lato', sans-serif" }}>
                        {d.field}
                      </p>
                      <span className="text-[11px] text-[#C9A961] whitespace-nowrap flex-shrink-0" style={{ fontFamily: "'Lato', sans-serif" }}>
                        {d.year}
                      </span>
                    </div>
                    <p className="text-xs text-[#B8A5A8] mt-1" style={{ fontFamily: "'Lato', sans-serif" }}>
                      {d.degree} &bull; {d.institution}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Certifications tab */}
        {activeTab === "certifications" && (
          <div className={`transition-all duration-500 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {certifications.map((c, i) => (
                <div
                  key={i}
                  className="flex items-start gap-4 p-5 rounded-xl bg-[#FAF7F2] border border-[#C9A961]/10 hover:border-[#C9A961]/35 transition-colors duration-300"
                >
                  <div className="w-10 h-10 flex-shrink-0 flex items-center justify-center rounded-full bg-[#C9A961]/12">
                    <i className="ri-award-line text-[#C9A961] text-base" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-sm font-medium text-[#2B1810] leading-snug" style={{ fontFamily: "'Lato', sans-serif" }}>
                        {c.name}
                      </p>
                      <span className="text-[11px] text-[#C9A961] whitespace-nowrap flex-shrink-0" style={{ fontFamily: "'Lato', sans-serif" }}>
                        {c.year}
                      </span>
                    </div>
                    <p className="text-xs text-[#B8A5A8] mt-1" style={{ fontFamily: "'Lato', sans-serif" }}>
                      {c.issuer}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {/* Cert highlight strip */}
            <div className="mt-10 p-6 rounded-2xl bg-[#FAF7F2] border border-[#C9A961]/20">
              <p className="text-[11px] uppercase tracking-[0.25em] text-[#C9A961] mb-3" style={{ fontFamily: "'Lato', sans-serif" }}>
                Highlighted Credentials
              </p>
              <p className="text-sm text-[#2B1810]/70 leading-relaxed italic" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "17px" }}>
                Certiport Python IT Specialist &bull; Certified in Cybersecurity (ISC2) &bull; Six Sigma Black Belt (Aveta Institute) &bull; NCBFAA Master Customs Specialist &bull; CompTIA Security+ SY0-701
              </p>
            </div>
          </div>
        )}

      </div>
    </section>
  );
}
